//
// # PDK Utility Functions
//
// Utilities for looking up layer and purpose names from PDK definitions.
// In v6, layers and purposes are identified by numeric IDs, and their
// string names must be looked up from the PDK.
//

import { Result, Ok, Err } from "CoreUtils";
import type { PDK, PDKLayer, PDKLayerPurpose } from "./pdk";

/**
 * Multiplier used for encoding layer/purpose into a single number.
 * Encoded value = layer * LAYER_ENCODING_MULTIPLIER + purpose
 * This allows up to 65535 purposes per layer and 65535 layers total.
 */
export const LAYER_ENCODING_MULTIPLIER = 65536;

/**
 * Get layer name from PDK by layer number
 * @returns Result with layer name or error if layer not found
 */
export function getLayerName(layerNumber: number, pdk: PDK): Result<string> {
  const layer = pdk.layers.find((l) => l.number === layerNumber);
  if (!layer) {
    return Err({
      why: "pdk-layer-not-found",
      layerNumber,
      availableLayers: pdk.layers.map((l) => `${l.number}:${l.name}`),
    });
  }
  return Ok(layer.name);
}

/**
 * Get purpose name from PDK by layer and purpose numbers
 * @returns Result with purpose name or error if layer or purpose not found
 */
export function getPurposeName(
  layerNumber: number,
  purposeNumber: number,
  pdk: PDK
): Result<string> {
  const layer = pdk.layers.find((l) => l.number === layerNumber);
  if (!layer) {
    return Err({
      why: "pdk-layer-not-found",
      layerNumber,
      availableLayers: pdk.layers.map((l) => `${l.number}:${l.name}`),
    });
  }
  const purpose = layer.purposes.find((p) => p.number === purposeNumber);
  if (!purpose) {
    return Err({
      why: "pdk-purpose-not-found",
      layerNumber,
      layerName: layer.name,
      purposeNumber,
      availablePurposes: layer.purposes.map((p) => `${p.number}:${p.name}`),
    });
  }
  return Ok(purpose.name);
}

/**
 * Get full layer.purpose name (e.g., "M1.drawing")
 * @returns Result with full layer.purpose name or error if layer or purpose not found
 */
export function getFullLayerName(
  layerNumber: number,
  purposeNumber: number,
  pdk: PDK
): Result<string> {
  return getLayerName(layerNumber, pdk).andThen((layerName) =>
    getPurposeName(layerNumber, purposeNumber, pdk).andThen((purposeName) =>
      Ok(`${layerName}.${purposeName}`)
    )
  );
}

/**
 * Get PDK layer by number
 * @returns PDKLayer or undefined if not found
 */
export function findLayer(layerNumber: number, pdk: PDK): PDKLayer | undefined {
  return pdk.layers.find((l) => l.number === layerNumber);
}

/**
 * Get PDK layer purpose by layer and purpose numbers
 * @returns PDKLayerPurpose or undefined if not found
 */
export function findPurpose(
  layerNumber: number,
  purposeNumber: number,
  pdk: PDK
): PDKLayerPurpose | undefined {
  const layer = findLayer(layerNumber, pdk);
  if (!layer) return undefined;
  return layer.purposes.find((p) => p.number === purposeNumber);
}

// ============================================================================
// OpenAccess (OA) Layer Lookup Utilities
// ============================================================================

/**
 * Result of looking up an OA layer/purpose in a PDK
 */
export interface OALayerLookupResult {
  layer: PDKLayer;
  purpose: PDKLayerPurpose;
  genAlphaLayer: number;
  genAlphaPurpose: number;
}

/**
 * Find a GenAlpha layer/purpose from OA layer and purpose numbers.
 * This is used when importing OA designs to map from OA's (layerNum, purposeNum)
 * to GenAlpha's layer system.
 *
 * @param oaLayerNum - OpenAccess layer number
 * @param oaPurposeNum - OpenAccess purpose number
 * @param pdk - The PDK containing layer definitions
 * @returns OALayerLookupResult if found, undefined otherwise
 */
export function findLayerByOA(
  oaLayerNum: number,
  oaPurposeNum: number,
  pdk: PDK
): OALayerLookupResult | undefined {
  for (const layer of pdk.layers) {
    // Check if this layer's OA export matches
    if (layer.exports?.oa !== oaLayerNum) continue;

    // Find the matching purpose
    for (const purpose of layer.purposes) {
      if (purpose.exports.oa === oaPurposeNum) {
        return {
          layer,
          purpose,
          genAlphaLayer: layer.number,
          genAlphaPurpose: purpose.number,
        };
      }
    }
  }
  return undefined;
}

/**
 * Build a lookup map for fast OA -> GenAlpha layer/purpose lookups.
 * Call this once when loading a PDK, then use the map for fast lookups during import.
 *
 * @param pdk - The PDK containing layer definitions
 * @returns Map from "oaLayer:oaPurpose" string key to OALayerLookupResult
 */
export function buildOALayerMap(pdk: PDK): Map<string, OALayerLookupResult> {
  const map = new Map<string, OALayerLookupResult>();

  for (const layer of pdk.layers) {
    const oaLayerNum = layer.exports?.oa;
    if (oaLayerNum === undefined) continue;

    for (const purpose of layer.purposes) {
      const oaPurposeNum = purpose.exports.oa;
      if (oaPurposeNum === undefined) continue;

      const key = `${oaLayerNum}:${oaPurposeNum}`;
      map.set(key, {
        layer,
        purpose,
        genAlphaLayer: layer.number,
        genAlphaPurpose: purpose.number,
      });
    }
  }

  return map;
}

/**
 * Fast lookup using a pre-built OA layer map.
 *
 * @param oaLayerNum - OpenAccess layer number
 * @param oaPurposeNum - OpenAccess purpose number
 * @param oaMap - Pre-built map from buildOALayerMap()
 * @returns OALayerLookupResult if found, undefined otherwise
 */
export function lookupOALayer(
  oaLayerNum: number,
  oaPurposeNum: number,
  oaMap: Map<string, OALayerLookupResult>
): OALayerLookupResult | undefined {
  const key = `${oaLayerNum}:${oaPurposeNum}`;
  return oaMap.get(key);
}

/**
 * Encode a GenAlpha layer and purpose into a single number for use in the
 * graphics layer system. This is the canonical encoding used throughout GenAlpha.
 *
 * @param genAlphaLayer - GenAlpha layer number
 * @param genAlphaPurpose - GenAlpha purpose number
 * @returns Encoded layer number (layer * LAYER_ENCODING_MULTIPLIER + purpose)
 */
export function encodeLayer(
  genAlphaLayer: number,
  genAlphaPurpose: number
): number {
  return genAlphaLayer * LAYER_ENCODING_MULTIPLIER + genAlphaPurpose;
}

/**
 * Flag bit (2^32) set on encoded layer values that use OA (OpenAccess) layer
 * numbers. When present, the flag must be stripped before decoding the
 * layer/purpose fields. Values without this flag use GenAlpha layer numbers
 * directly.
 */
const OA_LAYER_FLAG = 4294967296; // 2 ** 32

/**
 * A GDS-import fallback sentinel, or undefined if `encoded` is an ordinary
 * encoded layer.
 *
 * A GDS import run WITHOUT a PDK path cannot match a GDS (layer, datatype) to
 * any GenAlpha layer, and stores the raw pair as `(gdsLayer << 32) | gdsDatatype`
 * rather than losing it. See
 * `DevGodMode/scripts/remap_rf_top_gds_fallback_layers_to_asap7.ts`, which exists
 * to rewrite these onto real PDK layers for one such design.
 *
 * These values collide with `OA_LAYER_FLAG` above: both claim the 2^32 boundary,
 * and `decodeLayer` strips exactly one 2^32, so a sentinel decodes to a
 * meaningless layer number (`587 << 32` comes out as `586 << 16` = 38404096).
 * Anything reporting an unresolved layer to a human should call this FIRST and
 * report the GDS pair, because the decoded number appears nowhere in the data
 * and cannot be looked up in a PDK or a techfile.
 *
 * Deliberately only a description: nothing here decides what such a layer should
 * become, because for most of them the PDK metadata has no answer.
 *
 * @param encoded - Raw encoded layer value, BEFORE `decodeLayer`
 * @returns The GDS layer/datatype the importer could not map, or undefined
 */
export function gdsFallbackOf(
  encoded: number
): { gdsLayer: number; gdsDatatype: number } | undefined {
  if (encoded < OA_LAYER_FLAG) return undefined;
  return {
    gdsLayer: Math.floor(encoded / OA_LAYER_FLAG),
    gdsDatatype: encoded % OA_LAYER_FLAG,
  };
}

/**
 * Decode an encoded layer number back to GenAlpha layer and purpose.
 * Handles both GenAlpha-encoded values and OA-flagged values (with 2^32 bit set).
 *
 * @param encoded - Encoded layer number
 * @returns Object with layer and purpose numbers
 */
export function decodeLayer(encoded: number): {
  layer: number;
  purpose: number;
} {
  // Strip the OA flag if present — the underlying layer/purpose encoding is the same
  const stripped = encoded >= OA_LAYER_FLAG ? encoded - OA_LAYER_FLAG : encoded;
  return {
    layer: Math.floor(stripped / LAYER_ENCODING_MULTIPLIER),
    purpose: stripped % LAYER_ENCODING_MULTIPLIER,
  };
}
